/** @format */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormResponse from '../form-response';
import FormFields from '../fields';
import RepeatingFieldCollection from '../fields/repeating-field-collection';
import { FormProperties } from '../form-modals';
import {
  submitMarketoForm,
  submitFormRequest,
  trackHeapForm,
  checkForMultiSelectFields,
} from '../form-services';
import { useStateValue } from '@context/state-context';
import { useSafeTranslate } from '@hooks/use-safe-translate';
import { htmlToCharacterCheck } from '@services/string-transformation-services';
import ModalWrapper from '@components/modals/modal-wrapper';
import { safeCurrentLanguage } from '@services/drupal-services';
import ChineseDisclaimer from '../fields/chinese-disclaimer';
import PrivacyDisclaimer from '../fields/privacy-disclaimer';

/**
 * Context Form Container.
 *
 * @param props
 * @returns
 */
const ContextFormContainer = (props: FormProperties): JSX.Element => {
  const {
    formTitle,
    formDescription,
    formFields,
    formId,
    formType,
    productFormType,
    redirectUrl,
    submitButtonText,
    submitResponse,
    emailTo,
    emailSubject,
    replyToField,
    repeatingFields,
    repeatingMax,
    repeatingMin,
    repeatingAddButtonText,
    repeatingRemoveButtonText,
    repeatingTitle,
    repeatingArrayKey,
    requestType,
    requestUrl,
  } = props;

  const [submissionSuccessful, setSubmissionSuccessful] =
    useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({ mode: 'onChange' });

  const translatedSubmitButtonText = useSafeTranslate(submitButtonText);
  const translatedSubmit = useSafeTranslate('Submit', true);
  const submit = translatedSubmitButtonText
    ? translatedSubmitButtonText
    : translatedSubmit;
  const title = htmlToCharacterCheck(useSafeTranslate(formTitle, true));
  const translatedFormDescription = useSafeTranslate(formDescription, true);
  const [showModal, setShowModal] = useState(false);

  // Context only variables.
  const [{ contactToggle }, dispatch] = useStateValue();

  /**
   * Submit Request Form.
   */
  const submitForm = (data) => {
    setSubmissionSuccessful(true);

    trackHeapForm({
      title: formTitle,
      type: 'axios',
      id: 'header-context-form',
      region: 'header',
      location: window.location.href,
      submissionType: 'Form Submissions',
      marketoId: formId,
      productId: -1,
      data: data,
    });

    if (requestType === 'email' || requestType === 'axios') {
      const submissionData = {
        data: data,
        formFields: formFields,
        emailTo: emailTo,
        subject: emailSubject,
        replyToField: replyToField,
        requestType: requestType,
        requestUrl: requestUrl,
      };
      submitFormRequest(submissionData);
    } else {
      submitMarketoForm(formType, checkForMultiSelectFields(data));
    }

    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 500);
    }
    reset();
  };

  /**
   * Toggle Language Selector.
   */
  useEffect(() => {
    if (!showModal) {
      reset();
      setSubmissionSuccessful(false);
    }

    dispatch({
      type: 'toggleContactForm',
      formToggle: { contactFormOpen: showModal },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Sets state when Modal is open.
   */
  useEffect(() => {
    if (contactToggle.contactFormOpen) {
      setShowModal(true);
    }
  }, [contactToggle.contactFormOpen]);

  /**
   * Render.
   */
  return (
    <ModalWrapper
      showModal={contactToggle.contactFormOpen}
      setShowModal={setShowModal}
      className={'modal-width-80'}>
      <form
        aria-label={title}
        onSubmit={handleSubmit(submitForm)}
        id={formType}>
        {/* Submission Message */}
        {submissionSuccessful && <FormResponse message={submitResponse} />}

        {/* Form */}
        <div
          className={
            submissionSuccessful ? `form-container hide` : `form-container`
          }>
          <h2>{title}</h2>

          {formDescription && <p>{translatedFormDescription} </p>}

          <FormFields
            fieldList={formFields}
            register={register}
            errors={errors}
          />

          {repeatingMin > 0 && (
            <RepeatingFieldCollection
              title={repeatingTitle}
              addButtonText={repeatingAddButtonText}
              removeButtonText={repeatingRemoveButtonText}
              fieldCollection={repeatingFields}
              min={repeatingMin}
              max={repeatingMax}
              register={register}
              errors={errors}
              repeatingArrayKey={repeatingArrayKey}
            />
          )}

          {productFormType && (
            <input
              type="hidden"
              name="Products_of_Interest__c"
              id="Products_of_Interest__c"
              value={productFormType}
            />
          )}

          {safeCurrentLanguage() === 'zh-hans' ? (
            <ChineseDisclaimer register={register} errors={errors} />
          ) : (
            <PrivacyDisclaimer register={register} errors={errors} />
          )}

          <button
            value={submit}
            type="submit"
            className="btn-submit"
            formNoValidate>
            {submit}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default ContextFormContainer;
